/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-undef */
/**
 * 地图模式的map
 */
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { ApolloProvider, useApolloClient, useReactiveVar } from '@apollo/client'
import HouseCard from '@/components/houseCard/index'
import debounce from 'lodash/debounce'
import inRange from 'lodash/inRange'
import isEqual from 'lodash/isEqual'
import pull from 'lodash/pull'
import { usePrevious, useUpdateEffect } from 'ahooks'
import { GOOGLE_MAP_LOAD_STATUS_ENUM, GOOGLE_MAP_TYPE_ENUM } from '@/lib/mapConfig'
import { statsigLog } from '@/lib/statsigAndGa'
import { ExtraData, RentyAiPropertiesFilterBy } from '@/interface/message'
import { RentyAIProperty } from '@/interface/property'
import { currentMapFacility, chatActiveProperty } from '@/state/chatActiveProperty'
import { propertyNeedShowCardInMap } from '@/state/map'
import { MAP_FACILITY_ENUM } from '@/interface/map'

import { Box } from '@mui/material'
import { isFunction } from 'lodash'
import Facilities, { FacilitiesRef } from '../components/facilities'
import Marker from '../components/marker'
import {
  OverlayViewType,
  OverlayView,
  getOverlayViewClass,
  OVERLAY_VIEW_TYPE_ENUM
} from '../components/overlayView'
import MapComponent, { MapComponentRef, MapInstance, GoogleMap } from '../components/mapComponent'
import useMapBrowsingList from '../hooks/useMapBrowsingList'
import useMapFacilities from '../hooks/useMapFacilities'
import useMapProperties from '../hooks/useMapProperties'
import { getMapManualPosition } from '../hooks/useMapManualIdle'
import useSelectStreetViewPosition from '../hooks/useSelectStreetViewPosition'
import { CustomControlType, OnCustomControlChanged } from '../components/customControl'
import AerialVideoPlayer from '../components/aerialVideoPlayer'
import Filter from '../components/filter'

const HOUSE_MAP_ID = 'house_map_id'
// 用于避免服务端useLayoutEffect告警
const useBrowserLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : () => {}

export interface HouseMapProps {
  defaultProperties?: RentyAIProperty[]
  onFilterChange?: (filter: RentyAiPropertiesFilterBy) => void
  hoverShowCard?: boolean
  selectPropertyId?: string | number
  onSelectProperty?: (detail?: ExtraData['propertyDetail']) => void
  facilityType?: MAP_FACILITY_ENUM
}

export interface HouseMapRef {
  translateToMapCenter: (props: {
    center: { lat: number | string; lng: number | string }
    zoom?: number
    needNotInMap?: boolean // true：如果没有在地图中才进行移动 false: 都进行移动
  }) => void
  // filter变化导致地图需要重新渲染
  requestMapPropertiesByFilterChange: (filter: RentyAiPropertiesFilterBy) => void
  requestMapPropertiesByIdleChange: () => void
  resetMapToDefault: () => void
  getMapStatus: () => GOOGLE_MAP_LOAD_STATUS_ENUM
  showPropertyCard: (_propertyId: string | number) => void
}
const HouseMap = forwardRef<HouseMapRef, HouseMapProps>(
  (
    {
      defaultProperties = [],
      onFilterChange,
      onSelectProperty,
      selectPropertyId,
      hoverShowCard = false,
      facilityType: outerFacilityType
    },
    ref
  ) => {
    const mapId = useId()
    // 和map相关的都用ref 不能用state
    const mapComponentRef = useRef<MapComponentRef>(null)
    const overlayViewRef = useRef<OverlayViewType | null>(null)
    const mapInstanceRef = useRef<MapInstance | null>(null)
    const mapStatusRef = useRef<GOOGLE_MAP_LOAD_STATUS_ENUM>(GOOGLE_MAP_LOAD_STATUS_ENUM.INIT)
    const googleMapRef = useRef<GoogleMap | null>(null)
    const currentInfoBoxRef = useRef<OverlayView | null>(null)
    const currentInfoBoxIsHoverRef = useRef<boolean>(false)
    const currentMarkersRef = useRef<OverlayView[]>([])
    const facilitiesRef = useRef<FacilitiesRef | null>(null)
    const filterRef = useRef<RentyAiPropertiesFilterBy | null>(null)
    // isMapIdleRef 是否是手动移动地图引起的idle
    const isMapIdleRef = useRef(false)
    // 有些地方是闭包 不会更新properties引用，用ref和state都各表示一个
    const propertiesRef = useRef<RentyAIProperty[]>(defaultProperties)
    const [properties, setProperties] = useState<RentyAIProperty[]>(defaultProperties)

    // 受控state
    const [facilityType, setFacilityType] = useState<MAP_FACILITY_ENUM>(null)
    const preSelectPropertyId = usePrevious(selectPropertyId)
    // const preFilters = usePrevious(filter)
    const [mapTypes, setMapTypes] = useState<CustomControlType[]>(
      selectPropertyId ? [GOOGLE_MAP_TYPE_ENUM.ROAD_MAP, GOOGLE_MAP_TYPE_ENUM.STREET_VIEW] : []
    )
    const [showChildren, setShowChildren] = useState(true)
    const [showAerial, setShowAerial] = useState(false)
    // 外部state
    const needShowCardInMap = useReactiveVar(propertyNeedShowCardInMap)
    const stateActiveProperty = useReactiveVar(chatActiveProperty)
    // request
    const client = useApolloClient()
    const { streetViewPosition, setStreetViewPosition, queryStreetViewPositionById } =
      useSelectStreetViewPosition()
    const { getMapBrowsingList, requestMapBrowsingList } = useMapBrowsingList()
    const { facilities, facilityLoading, getFacilitiesByPropertyId } = useMapFacilities()
    const { requestMapProperties, loading: requestPropertiesLoading } = useMapProperties()

    useImperativeHandle(ref, () => ({
      // 暴露给外部移动地图
      translateToMapCenter: props => {
        const { center, zoom, needNotInMap = false } = props
        const position = { lat: Number(center.lat), lng: Number(center.lng) }
        const map = mapInstanceRef.current
        if (!map) return
        const idlePosition = getMapManualPosition(map)
        if (!idlePosition) return
        const isInMap =
          inRange(
            position.lat,
            Number(idlePosition.bottomRight.lat),
            Number(idlePosition.topLeft.lat)
          ) &&
          inRange(
            position.lng,
            Number(idlePosition.topLeft.lon),
            Number(idlePosition.bottomRight.lon)
          )
        const needTranslate = !needNotInMap || (needNotInMap && !isInMap)
        if (needTranslate) {
          map.setCenter(position)
          if (zoom) map.setZoom(zoom)
        }
      },
      showPropertyCard,
      requestMapPropertiesByFilterChange: filter => {
        // 如果和上次请求的filter相同 则取消请求
        if (isEqual(filter, filterRef.current) || !filter) return
        filterRef.current = filter
        reRequestProperties('filter', filter)
      },
      requestMapPropertiesByIdleChange: () => {
        const map = mapInstanceRef.current
        if (!map) return
        const idlePosition = getMapManualPosition(map)
        const manualFilter = {
          rentyAILocation: idlePosition,
          cityEqualTo: ''
        }
        reRequestProperties('idle', manualFilter)
      },
      getMapStatus: () => mapStatusRef.current,
      resetMapToDefault: () => {
        setMapTypes([])
        const map = mapInstanceRef.current
        if (map) {
          const streetView = map.getStreetView()
          streetView.setVisible(false)
        }
      }
    }))
    // 显示房源卡片
    const showPropertyCard = (hoverId: string | number) => {
      if (currentInfoBoxRef.current) {
        if (String(hoverId) === String(currentInfoBoxRef.current.id)) return
        currentInfoBoxRef.current.setMap(null)
        currentInfoBoxRef.current = null
      }
      if (!hoverId || !mapInstanceRef.current || !overlayViewRef.current) return
      const OverlayViewClass = overlayViewRef.current
      const property = propertiesRef.current.find(({ id }) => `${id}` === `${hoverId}`)
      if (!property) return
      if (facilitiesRef.current) {
        facilitiesRef.current.hideFacilityCard()
      }
      const card = new OverlayViewClass({
        position: { latitude: property.latitude, longitude: property.longitude },
        type: OVERLAY_VIEW_TYPE_ENUM.INFO_BOX,
        content: (
          <ApolloProvider client={client}>
            <HouseCard
              className="w-[300px]"
              onMouseEnter={() => {
                currentInfoBoxIsHoverRef.current = true
              }}
              onMouseLeave={() => hideSelectedCard()}
              property={property}
              handleClick={onSelectProperty}
            />
          </ApolloProvider>
        ),
        id: property.id,
        offset: { x: 40, y: 10 }
      })
      card.containerDiv.style.zIndex = 11
      card.setMap(mapInstanceRef.current)
      currentInfoBoxRef.current = card
    }
    // 隐藏房源卡片 id为undefined隐藏全部 isForce指的是否强制隐藏
    const hideSelectedCard = (isForce = true, id: string | number | undefined = undefined) => {
      if (!currentInfoBoxRef.current) return
      if (typeof isForce === 'boolean' && isForce) {
        currentInfoBoxIsHoverRef.current = false
        currentInfoBoxRef.current.setMap(null)
        currentInfoBoxRef.current = null
      } else {
        if (String(id) !== String(currentInfoBoxRef.current.id)) return
        if (currentInfoBoxIsHoverRef.current) return
        currentInfoBoxRef.current.setMap(null)
        currentInfoBoxRef.current = null
      }
    }
    // 点击marker以后
    const onClickMarker = (id: string | number, property: ExtraData['propertyDetail']) => {
      if (String(id) === String(selectPropertyId)) return
      statsigLog('map_property_click', {
        property_id: id
      })
      if (onSelectProperty) {
        onSelectProperty(property)
      }
    }
    // 在地图上添加markers
    const addMarkers = (
      addProperties: RentyAIProperty[],
      addSelectPropertyId: string | number | undefined,
      hovered?: boolean
    ) => {
      if (!mapInstanceRef.current || !overlayViewRef.current) return
      const OverlayViewClass = overlayViewRef.current
      const tempOverlayView: OverlayView[] = []
      const browsingList = getMapBrowsingList()
      if (!Array.isArray(addProperties)) return
      addProperties.forEach(property => {
        const marker = new OverlayViewClass({
          position: { latitude: property.latitude, longitude: property.longitude },
          type: OVERLAY_VIEW_TYPE_ENUM.MARKER,
          content: (
            <Marker
              key={property.id}
              property={property}
              browsed={browsingList.includes(String(property.id))}
              select={!!addSelectPropertyId && String(addSelectPropertyId) === String(property.id)}
              hovered={hovered || false}
              onClick={onClickMarker}
              onRemove={id => {
                hideSelectedCard(false, id)
              }}
              onHover={id => {
                if (hoverShowCard) {
                  showPropertyCard(id)
                }
              }}
            />
          ),
          id: property.id
        })
        tempOverlayView.push(marker)
      })

      tempOverlayView.forEach(marker => marker.setMap(mapInstanceRef.current))
      currentMarkersRef.current = [...currentMarkersRef.current, ...tempOverlayView]
    }
    // 移除地图上给定的房源id marker
    const removerMarkers = (ids: (string | number)[]) => {
      ids.forEach(id => {
        const currentMarker = currentMarkersRef.current.find(
          marker => String(marker.id) === String(id)
        )
        if (currentMarker) {
          currentMarker.setMap(null)
          pull(currentMarkersRef.current, currentMarker)
        }
      })
    }
    // 移除已经不存在的房源marker 增加没有被渲染的房源marker
    const diffMarker = (diffProperties: RentyAIProperty[]) => {
      const ids = diffProperties.map(p => String(p.id))
      const needRemoveMarkers = currentMarkersRef.current.filter(marker => !ids.includes(marker.id))
      needRemoveMarkers.forEach(marker => marker.setMap(null))
      const restMarkers = currentMarkersRef.current.filter(marker => ids.includes(marker.id))
      const restIds = restMarkers.map(m => String(m.id))
      const needAddProperties = diffProperties.filter(p => !restIds.includes(String(p.id)))
      currentMarkersRef.current = restMarkers
      addMarkers(needAddProperties, selectPropertyId)
    }
    // 移除地图上所有房源的marker
    const removeAllMarker = () => {
      currentMarkersRef.current.forEach(marker => marker.setMap(null))
      currentMarkersRef.current = []
    }
    const changeSelectedMarker = () => {
      const selected = propertiesRef.current.find(
        property => String(property.id) === String(selectPropertyId)
      )
      const preSelected = propertiesRef.current.find(
        property => String(property.id) === String(preSelectPropertyId)
      )
      const target: RentyAIProperty[] = []
      if (selected) {
        target.push(selected)
      }
      if (preSelected) {
        target.push(preSelected)
      }
      if (facilityType && selectPropertyId) {
        removeAllMarker()
        addMarkers(selected ? [selected] : [], selectPropertyId)
      } else {
        removerMarkers(target.map(t => t.id))
        addMarkers(target, selectPropertyId)
      }
      showPropertyCard(selectPropertyId)
    }

    // 渲染房源标签到地图上
    const renderMarkerOnMap = (renderProperties: RentyAIProperty[], transform = false) => {
      if (Array.isArray(renderProperties) && renderProperties.length > 0) {
        if (facilityType && selectPropertyId) {
          removeAllMarker()
          addMarkers(
            renderProperties.filter(property => String(property.id) === String(selectPropertyId)),
            selectPropertyId
          )
        } else {
          diffMarker(renderProperties)
        }
        if (renderProperties.every(property => String(property.id) !== String(selectPropertyId))) {
          hideSelectedCard()
        }
        if (transform) {
          autoTransform(renderProperties)
        }
      }
    }
    // 基础设施type变化
    const onFacilityTypeChanged = (currentType: MAP_FACILITY_ENUM | null) => {
      setFacilityType(currentType)
      currentMapFacility(currentType)
      // 如果进入基础设施模式 隐藏其他房源
      if (currentType && selectPropertyId) {
        removerMarkers(
          propertiesRef.current
            .map(property => property.id)
            .filter(id => String(id) !== String(selectPropertyId))
        )
        return
      }
      // 如果退出了基础设施模式 显示其他房源
      if (!currentType) {
        if (currentMarkersRef.current.length <= 1 && propertiesRef.current.length > 1) {
          renderMarkerOnMap(propertiesRef.current, false)
        }
      }
    }

    // 手动移动地图 经纬度找房
    const onManualMapIdle = debounce(() => {
      if (!mapInstanceRef.current) return
      const idlePosition = getMapManualPosition(mapInstanceRef.current)
      const manualFilter = {
        rentyAILocation: idlePosition,
        cityEqualTo: ''
      }
      const bounds = mapInstanceRef.current.getBounds()
      if (!bounds) {
        return
      }
      setFacilityType(null)
      currentMapFacility(null)
      reRequestProperties('idle', manualFilter)
    }, 300)
    // 根据房源信息自动缩放移动地图 移动后需要将mapIdle设置为false
    const autoTransform = (autoProperties: RentyAIProperty[]) => {
      if (!mapInstanceRef.current || !googleMapRef.current) return
      if (Array.isArray(autoProperties) && autoProperties.length > 0) {
        const bounds = new googleMapRef.current.LatLngBounds()
        autoProperties.forEach(
          ({ latitude, longitude }) =>
            googleMapRef.current &&
            bounds.extend(new googleMapRef.current.LatLng(Number(latitude), Number(longitude)))
        )
        const padding = autoProperties.length > 5 ? 40 : 100
        mapInstanceRef.current.setCenter(bounds.getCenter())
        mapInstanceRef.current.fitBounds(bounds, padding)
        isMapIdleRef.current = false
      }
    }
    // 改变地图类型
    const changeMapType = () => {
      let curMapTypes: CustomControlType[] = []
      if (
        selectPropertyId &&
        propertiesRef.current.some(p => String(p.id) === String(selectPropertyId))
      ) {
        const selectProperty = propertiesRef.current.find(
          p => String(p.id) === String(selectPropertyId)
        )
        if (selectProperty) {
          curMapTypes = [GOOGLE_MAP_TYPE_ENUM.ROAD_MAP, GOOGLE_MAP_TYPE_ENUM.STREET_VIEW]
          const webAerial =
            Array.isArray(selectProperty.aerialVideo) &&
            selectProperty.aerialVideo.find(({ name }) => name === 'web')

          if (webAerial && webAerial.url) {
            curMapTypes.push('aerial')
          }
        }
      }
      setMapTypes(curMapTypes)
    }
    // 地图自定义控制触发的时候
    const onMapControlChanged: OnCustomControlChanged = (type, { mapType, zoom }) => {
      let eventName = ''
      switch (type) {
        case 'zoom_minus':
          eventName = 'main_map_zoomin_click'
          break
        case 'zoom_plus':
          eventName = 'main_map_zoomout_click'
          break
        case 'type':
        default:
          eventName = 'main_map_view_type_click'
      }
      statsigLog(eventName, {
        type: mapType,
        zoom
      })
      if (type === 'type' && mapType === 'aerial') {
        // 航拍
        setShowAerial(true)
      } else {
        setShowAerial(false)
      }
    }
    const onAerialControlChange: OnCustomControlChanged = (type, { mapType }) => {
      if (!mapInstanceRef.current) return
      if (mapType === 'aerial') return
      setShowAerial(false)
      const map = mapInstanceRef.current
      const streetView = map.getStreetView()
      if (mapType === GOOGLE_MAP_TYPE_ENUM.STREET_VIEW) {
        streetView.setPosition(streetViewPosition)
        streetView.setVisible(true)
        setShowChildren(false)
      } else {
        streetView.setVisible(false)
        setShowChildren(true)
      }
    }
    // 航拍的url
    const aerialVideo = useMemo(() => {
      const selected = propertiesRef.current.find(
        property => String(property.id) === String(selectPropertyId)
      )
      if (!selected) return []
      const webAerial =
        Array.isArray(selected.aerialVideo) && selected.aerialVideo.find(v => v.name === 'web')
      return webAerial && webAerial.url ? [webAerial] : []
    }, [selectPropertyId, propertiesRef.current])

    // rental 数量
    const rentalNum = useMemo(() => {
      let count = 0
      propertiesRef.current.forEach(property => {
        count += property.availableUnitNum ? Number(property.availableUnitNum) : 0
      })
      return count
    }, [propertiesRef.current])

    // 是否是详情模式 详情模式下隐藏filter
    const isDetailPropertyMode = useMemo(
      () => stateActiveProperty && stateActiveProperty.id,
      [stateActiveProperty]
    )
    // 改变街景模式的位置
    const changeStreetViewPosition = async () => {
      const selected = propertiesRef.current.find(
        property => String(property.id) === String(selectPropertyId)
      )
      if (selectPropertyId && selected) {
        setStreetViewPosition({
          lat: Number(selected.latitude),
          lng: Number(selected.longitude)
        })
        queryStreetViewPositionById(selectPropertyId)
        return
      }
      setStreetViewPosition(undefined)
    }
    // 根据config初始化地图
    const init = async (options?: google.maps.MapOptions) => {
      if (mapComponentRef.current) {
        await mapComponentRef.current.init(options).then(({ status, map, GoogleMap: x }) => {
          googleMapRef.current = x
          mapInstanceRef.current = map
          mapStatusRef.current = status
          overlayViewRef.current = getOverlayViewClass()
        })
      }
    }
    // 重新请求房源数据重新渲染 只有两种情况重新请求：1.【手动】移动地图 2. 外部筛选条件变化
    const reRequestProperties = async (
      type: 'filter' | 'idle',
      currentFilter: RentyAiPropertiesFilterBy
    ) => {
      if (type === 'filter' && currentFilter) {
        isMapIdleRef.current = false
        const { list } = await requestMapProperties(currentFilter)
        setProperties(list)
        propertiesRef.current = list
        setTimeout(() => renderMarkerOnMap(list, true))
      }
      if (type === 'idle' && currentFilter) {
        isMapIdleRef.current = true
        const { list } = await requestMapProperties(currentFilter)
        setProperties(list)
        propertiesRef.current = list
        setTimeout(() => renderMarkerOnMap(list, false))
      }
    }

    // 检测浏览过的marker，赋值给browsingList
    const checkBrowsingMarker = () => {
      const browsingList = getMapBrowsingList()
      if (browsingList.includes(String(selectPropertyId))) return
      browsingList.push(String(selectPropertyId))
    }

    // 选中的房源变化后
    useBrowserLayoutEffect(() => {
      checkBrowsingMarker()
      changeMapType()
      changeStreetViewPosition()
      changeSelectedMarker()

      getFacilitiesByPropertyId(selectPropertyId)
    }, [selectPropertyId, properties])

    useUpdateEffect(() => {
      setShowAerial(false)
    }, [selectPropertyId])
    // 初始化地图
    useBrowserLayoutEffect(() => {
      Promise.resolve(init({ maxZoom: 16 })).then(async () => {
        await Promise.allSettled([requestMapBrowsingList()])
      })
    }, [])

    // 设施type facility houseMap层受控
    useEffect(() => {
      onFacilityTypeChanged(outerFacilityType)
    }, [outerFacilityType])

    useEffect(() => {
      const { propertyId = '', prePropertyId = '' } = needShowCardInMap || {
        propertyId: '',
        prePropertyId: ''
      }
      hideSelectedCard()
      if (propertyId) {
        const needShowCardInMapProperty = propertiesRef.current.find(
          p => String(p.id) === String(propertyId)
        )
        if (needShowCardInMapProperty) {
          showPropertyCard(propertyId)
          removerMarkers([propertyId])
          addMarkers([needShowCardInMapProperty], selectPropertyId, true)
        }
      }
      // 取消上一个的hover效果
      if (prePropertyId) {
        const needShowCardInMapPreProperty = propertiesRef.current.find(
          p => String(p.id) === String(prePropertyId)
        )
        if (needShowCardInMapPreProperty) {
          removerMarkers([prePropertyId])
          addMarkers([needShowCardInMapPreProperty], selectPropertyId)
        }
      }
    }, [needShowCardInMap])

    return (
      <Box component="div" className="w-full h-full flex flex-col">
        {mapStatusRef.current === GOOGLE_MAP_LOAD_STATUS_ENUM.SUCCESS && (
          <Filter
            rentalNum={rentalNum}
            showFilter={!isDetailPropertyMode}
            showRental={!isDetailPropertyMode || !requestPropertiesLoading}
            onFilterChange={filter => {
              reRequestProperties('filter', filter)
              if (isFunction(onFilterChange)) {
                onFilterChange(filter)
              }
            }}
          />
        )}

        <MapComponent
          className="flex-1"
          ref={mapComponentRef}
          id={`${HOUSE_MAP_ID}_${mapId}`}
          onClick={() => hideSelectedCard()}
          mapTypes={mapTypes}
          showChildren={showChildren}
          onControlChanged={onMapControlChanged}
          loading={requestPropertiesLoading}
          onIdle={() => {
            // 非手动移动地图引起的变化，在业务中需要把地图移动模式设置为false
            if (isMapIdleRef.current === false) {
              isMapIdleRef.current = true
              return
            }
            onManualMapIdle.cancel()
            onManualMapIdle()
          }}
          streetViewPosition={streetViewPosition}
        >
          {selectPropertyId && (
            <Facilities
              ref={facilitiesRef}
              map={mapInstanceRef.current}
              facilities={facilities}
              loading={facilityLoading}
              type={facilityType}
              onTypeChanged={onFacilityTypeChanged}
              onShowFacilityInfoCard={() => hideSelectedCard()}
              className="absolute left-3 bottom-3"
            />
          )}
          {showAerial && aerialVideo.length > 0 && (
            <AerialVideoPlayer
              url={aerialVideo[0].url}
              map={mapInstanceRef.current}
              onControlChanged={onAerialControlChange}
            />
          )}
        </MapComponent>
      </Box>
    )
  }
)

HouseMap.displayName = 'HouseMap'
export default HouseMap
